import { IBankAccountInfo } from "@/apps/credit-line/models/IBankAccountInfo";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";

export interface IPayment {
  paymentAmount: number;
  currency: string;
  paymentMethod: string;
  paymentInitiationTime: number;
  paymentReceivedTime: number;
  principalPaid: number;
  interestPaid: number;
  taxPaid: number;
}

export enum CreditLineDebitType {
  WITHDRAWAL = "WITHDRAWAL",
  CREDITCARD = "CREDITCARD",
  CASH_TRANSFER = "CASH_TRANSFER",
  PHONE_PURCHASE = "PHONE_PURCHASE",
  PHONE_FINANCE = "PHONE_FINANCE",
  REVERSAL_PURCHASE = "REVERSAL_PURCHASE",
  REFUND_REVERSAL = "REFUND_REVERSAL",
  PURCHASE_ADJUSTMENT = "PURCHASE_ADJUSTMENT",
}

export enum TransactionStatus {
  READY = "READY",
  EXPIRED = "EXPIRED",
  REJECTED = "REJECTED",
  CANCELED = "CANCELED",
  PAID = "PAID",
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  COMPLETE = "COMPLETE",
}

export enum TransactionType {
  WITHDRAWAL = "WITHDRAWAL",
  CREDITCARD = "CREDITCARD",
  CASH_TRANSFER = "CASH_TRANSFER",
  PHONE_PURCHASE = "PHONE_PURCHASE",
  PHONE_FINANCE = "PHONE_FINANCE",
  REVERSAL_PURCHASE = "REVERSAL_PURCHASE",
  REFUND_REVERSAL = "REFUND_REVERSAL",
  PURCHASE_ADJUSTMENT = "PURCHASE_ADJUSTMENT",
  PAYMENT = "PAYMENT",
  PAYMENT_SCHEDULE = "PAYMENT_SCHEDULE",
  PAYMENT_REMAINDER = "PAYMENT_REMAINDER",
  REFUND = "REFUND",
  CASH_BACK = "CASH_BACK",
  BUNDLE = "BUNDLE",
}

export enum WithdrawalStatusValues {
  ENABLED = "ENABLED",
  FROZEN = "FROZEN",
  FROZEN_PERMANENTLY = "FROZEN_PERMANENTLY",
}

export interface IPickupInformation {
  pickupMethod: string;
  contract: number;
  concept: string;
  reference: number;
  expirationTime: number;
}

export interface ITransaction {
  paymentId?: string;
  paymentMethod?: string;
  paymentAmount?: number | string;
  currency?: string;
  paymentInitiationTime?: number;
  paymentReceivedTime?: number;
  principalPaid?: number | string;
  interestPaid?: number | string;
  taxPaid?: number | string;
  amount?: string;
  creditId?: number;
  transactionTime?: number;
  creditSource?: string;
  originalDebitId?: number;
  date: number;
  type?: TransactionType;
  method?: string;
  category?: "moneyIn" | "moneyOut";
  debitId?: number;
  status: TransactionStatus;
  requestedTime?: number;
  disbursedTime?: number;
  originalDisbursedTime?: number;
  expirationTime?: number;
  message?: string;
  annualPercentageRate?: number;
  weeklyPercentageRate?: string;
  paymentTermUnit?: string;
  debitSource?: string;
  pickupInformation?: IPickupInformation;
  cardType?: string;
  cardId?: number;
}

export interface IWithdrawal {
  debitId: number;
  amount: number;
  currency: string;
  status: TransactionStatus;
  debitSource: string;
  type?: CreditLineDebitType;
  annualPercentageRate: number;
  pickupInformation: IPickupInformation;
  originalDisbursedTime?: number;
  disbursedTime?: number;
  expirationTime?: number;
  weeklyPercentageRate?: string;
  requestedTime?: number;
  verificationCode?: string;
  cardType?: string;
  cardId?: number;
}

export interface ICredit {
  creditId: number;
  status: TransactionStatus;
  amount: string;
  transactionTime: number;
  message: string;
  creditSource: string;
  originalDebitId: number;
  type: TransactionType;
  currency: string;
}

export interface IPaymentSchedule {
  payment: number;
  dueDate: number;
  minimumPaymentAmount: string;
  principal: string;
  interest: string;
  tax: string;
  fees: string;
  currency: string;
  date: string;
  endBalance: string;
  type: TransactionType;
}

export interface IUserInfo {
  firstName: string;
  lastName: string;
  idNumber: string;
  gender: string;
  birthDate: string;
  email: string;
  phone: string;
  country: string;
}

export interface IAddressInfo {
  city: string;
  floor: string;
  region: string;
  zip_code: string;
  apartment: string;
  street_name: string;
  neighborhood: string;
  street_number: string;
}

export interface IWithdrawalConditions {
  flagWindowPickup: boolean;
  windowPickupDisabledReason: string;
  flagBankTransfer: boolean;
  withinBankOperationHours: boolean;
}

export interface ICreditLinePromotion {
  image: string;
  expiration_date: number;
  internalLink: string;
  offeredLimitAmount?: number;
  functionality?: string;
}

export interface ICardInfo {
  externalCreditCardId: string;
  status: PayJoyCardStatus;
  shipmentId?: string;
  type: string;
  lastFour: string;
}

export interface IShipmentInfo {
  status: PayJoyCardStatus;
  shipmentId?: string;
  type: string;
}

export interface IFeature {
  name?: string; // Optional for new structure. Will be removed
  bucket: number;
}
export type ServerRequestableActions = "CreateVirtualCardOnDemand";

export type DeviceCohort =
  | "CC_REQUESTED_NOT_RECEIVED"
  | "CC_RECEIVED_NOT_ACTIVATED"
  | "CC_ACTIVATED"
  | "CC_EXISTED_NOT_ACTIVE"
  | "CC_NEVER_REQUESTED"
  | null;

export enum CustomerType {
  PLAY_STORE = "play store",
  KIOSK = "kiosk",
  PRIOR_BORROWER = "prior_borrower",
  PJM_PRIOR_BORROWER = "pjm_prior_borrower",
  JOINT_LOAN_LINE = "joint_loan_line",
  SIMULTANEOUS_LOAN_LINE = "simultaneous_loan_line",
}

export type Message = {
  title: string;
  body: string;
  button_text: string;
  type: string;
  functionality: string;
  extras?: {
    limitIncreaseAmount?: string;
    expirationDateUnixTimestamp?: string;
    unformattedLimitIncreaseAmount?: string;
  };
  internalLink: string;
};

export interface ICreditLineDetails {
  address: IAddressInfo; // Legacy support. Will be removed.
  allowUnFreezingWithdrawals?: boolean;
  annualPercentageRate: number;
  availableCredit: number;
  bankAccountInfo?: IBankAccountInfo;
  cards?: ICardInfo[];
  ccTrackingUrl?: string;
  closeTime?: number;
  country: CL_COUNTRY;
  createTime?: number;
  creditCardStatus: PayJoyCardStatus;
  creditLimit: number;
  creditLineId: number;
  credits: ICredit[];
  currency: string;
  currencySymbol: string;
  currentPeriodBalance: number;
  currentPeriodValidThrough: number;
  customerName: string;
  customerPhone?: string | null;
  customerType: CustomerType;
  deviceId?: number;
  experiments: IFeature[] | Record<string, IFeature>;
  expiry: number;
  features?: IFeature[] | Record<string, IFeature>;
  hashValue: string;
  isBankTransferEnabled?: boolean;
  isNewShipmentsUiEnabled?: boolean;
  isReportingTransactionsEnabled?: boolean;
  isTester: boolean;
  isVirtualCardEnabled?: boolean;
  minimumPaymentAmount: number;
  minimumWithdrawalAmount?: number;
  messages?: Message[];
  nextPeriodValidThrough: number;
  payableToday?: number;
  paymentSchedule: IPaymentSchedule[];
  paymentTerm?: number;
  paymentTermUnit?: string;
  payments: IPayment[];
  payoffBalanceToday: number;
  previousPeriodBalance: number;
  previousPeriodValidThrough: number;
  promotion?: ICreditLinePromotion;
  promotions?: ICreditLinePromotion[];
  serverRequestedActions?: ServerRequestableActions[];
  shipmentId?: string;
  shipments?: IShipmentInfo[];
  status?: string;
  tomorrowMinPayment?: number;
  totalInterestSaved?: number;
  user: IUserInfo;
  userId?: number;
  withdrawalConditions?: IWithdrawalConditions;
  withdrawalStatus?: WithdrawalStatusValues;
  withdrawals: IWithdrawal[];
}
